import {
    isWeekend,
    startOfMonth,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears
} from 'date-fns';
import { MarketValue } from './market-value.model';
import { MarketValuesPeriodData } from './market-value-period-data.model';

const transformData = (data: MarketValue[]): Record<string, MarketValuesPeriodData> => {
    const periods = {
        '1D': { order: 0, name: '1D', hide: true },
        '1W': { order: 1, name: '1W', hide: false },
        MTD: { order: 2, name: 'MTD', hide: false },
        '1M': { order: 3, name: '1M', hide: false },
        '3M': { order: 4, name: '3M', hide: false },
        YTD: { order: 5, name: 'YTD', hide: false },
        '1Y': { order: 6, name: '1Y', hide: false },
        '3Y': { order: 7, name: '3Y', hide: false },
        '5Y': { order: 8, name: '5Y', hide: false },
        All: { order: 9, name: 'All', hide: false }
    };

    const result: Record<string, MarketValuesPeriodData> = {};

    for (const [period, periodData] of Object.entries(periods)) {
        const filteredData = filterDataByPeriod(data, period);
        if (filteredData.length > 0) {
            const marketValue = filteredData[filteredData.length - 1].marketValue;
            const startValue =
                filteredData[0].marketValue ||
                filteredData.find((d) => d.marketValue !== 0)?.marketValue;
            const absoluteChange = marketValue - startValue;
            const relativeChange = absoluteChange / startValue;
            const volatility = calculateVolatility(filteredData);
            const high = Math.max(...filteredData.map((d) => d.marketValue));
            const low = Math.min(...filteredData.map((d) => d.marketValue));
            result[period] = {
                ...periodData,
                marketValue,
                relativeChange,
                absoluteChange,
                volatility,
                high,
                low
            };
        }
    }

    return result;
};

const filterDataByPeriod = (data: MarketValue[], period: string): MarketValue[] => {
    if (data.length === 0) {
        return [];
    }
    const lastValueDate = data[data.length - 1]?.valueDate;
    let startDate: number;

    switch (period) {
        case '1D':
            startDate = subDays(lastValueDate, 1).getTime();
            break;
        case '1W':
            startDate = subDays(subWeeks(lastValueDate, 1), 0).getTime();
            break;
        case 'MTD':
            startDate = subDays(startOfMonth(lastValueDate), 0).getTime();
            break;
        case '1M':
            startDate = subDays(subMonths(lastValueDate, 1), 0).getTime();
            break;
        case '3M':
            startDate = subDays(subMonths(lastValueDate, 3), 0).getTime();
            break;
        case 'YTD':
            startDate = subDays(startOfYear(lastValueDate), 1).getTime();
            break;
        case '1Y':
            startDate = subDays(subYears(lastValueDate, 1), 0).getTime();
            break;
        case '3Y':
            startDate = subDays(subYears(lastValueDate, 3), 0).getTime();
            break;
        case '5Y':
            startDate = subDays(subYears(lastValueDate, 5), 0).getTime();
            break;
        case 'All':
            startDate = data[0]?.valueDate;
            break;
    }

    startDate = getLastWeekday(new Date(startDate)).getTime();
    return data.filter((d) => d.valueDate >= startDate);
};

const calculateVolatility = (data: MarketValue[]): number => {
    const marketValues = data.map((d) => d.marketValue);
    const mean =
        marketValues.reduce((acc, price) => acc + price, 0) / marketValues.length;
    const variance =
        marketValues.reduce((acc, price) => acc + Math.pow(price - mean, 2), 0) /
        marketValues.length;
    return Math.sqrt(variance) / mean;
};

const getLastWeekday = (date: Date): Date => {
    if (isWeekend(date)) {
        return getLastWeekday(subDays(date, 1));
    } else {
        return date;
    }
};

export { transformData, calculateVolatility, getLastWeekday, filterDataByPeriod };
